import { MdCheck, MdEco, MdEdit, MdStar } from 'react-icons/md';
import { IconButton } from '@mui/material';
import { formatRelativeTime } from '@utils/format';
import { hideOnImgError, imageUrl } from '@utils/imageUrl';
import type { Crop } from '../schema';

interface Props {
  row: Crop;
  onEdit: (row: Crop) => void;
}

/**
 * Mobile card view for a crop row. Rendered by `<ResponsiveDataList>` below
 * md (768px). Tap → open edit dialog.
 *
 * Layout:
 *   [thumb]  Cotton                            [● Active]
 *            cotton · fiber                    [⭐ Preselected]
 *            Updated 2d ago
 */
export function CropCard({ row, onEdit }: Props) {
  const thumb = imageUrl(row.thumbnailStorageKey ?? row.imageStorageKey);

  return (
    <div className="group relative flex gap-3 rounded-xl border border-border bg-surface p-3 transition-colors hover:border-primary/40 hover:bg-primary/[0.02]">
      <button
        type="button"
        onClick={() => onEdit(row)}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label={`Edit ${row.name}`}
      />

      <div className="relative z-10 shrink-0">
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="h-14 w-14 rounded-lg object-cover ring-1 ring-border"
            onError={hideOnImgError}
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-surface-muted text-foreground-muted ring-1 ring-border">
            <MdEco size={22} />
          </div>
        )}
      </div>

      <div className="relative z-10 min-w-0 flex-1 pointer-events-none">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate text-[15px] font-semibold leading-tight text-foreground">
            {row.name}
          </h3>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {row.isActive ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-success/[0.14] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
                <MdCheck size={11} />
                Active
              </span>
            ) : (
              <span className="rounded-full bg-foreground-muted/[0.14] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground-muted">
                Inactive
              </span>
            )}
            {row.isPreselected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent/[0.14] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                <MdStar size={11} />
                Preselected
              </span>
            )}
          </div>
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-foreground-muted">
          <code className="rounded bg-surface-muted px-1 py-0.5 font-mono text-[11.5px] text-foreground">
            {row.code}
          </code>
          {row.category && <span> · {row.category}</span>}
          {row.displayOrder != null && <span> · #{row.displayOrder}</span>}
        </div>
        <div className="mt-1 text-[11px] text-foreground-muted">
          Updated {formatRelativeTime(row.updatedAt)}
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 items-center">
        <IconButton
          size="small"
          onClick={() => onEdit(row)}
          aria-label={`Edit ${row.name}`}
        >
          <MdEdit size={18} />
        </IconButton>
      </div>
    </div>
  );
}
